/**
 * 消息信封 `meshlink/chat/1.0`（迁移自 `prototype/web/core/crypto.js`，实现重写）。
 *
 * 与原型的差异：
 *  1. 签名对象改为 **canonical JSON**（键有序、无空格、剔除 signature 字段）—— 跨端可复现
 *  2. 收端严格 **先验签后解密**，验签失败直接抛 `E_SIGNATURE_INVALID`（S6）
 *  3. Ed25519 改由 @noble 实现（规避 WebCrypto 浏览器门槛）
 */

import { ed25519Sign, ed25519Verify } from './noble';
import { canonicalBytes } from './canonical';
import { bytesToBase64, base64ToBytes } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import type { ChatEnvelope } from '@shared/types';

export const ENVELOPE_PROTOCOL = 'meshlink/chat/1.0' as const;
export type EnvelopeKind = ChatEnvelope['kind'];

/** 生成消息 ID：`msg_<base36 时间>_<随机>`（与原型 msgId 兼容）。 */
export function newMessageId(): string {
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export interface BuildEnvelopeInput {
  kind: EnvelopeKind;
  from: string;
  to: string;
  /** 我的签名私钥种子（32B） */
  signSeed: Uint8Array;
  /** 我的 X25519 公钥（base64） */
  encPub: string;
  sequence: number;
  /** 加密后的密文（base64），intro/ack/read/signal 无需传 */
  ciphertext?: string;
  iv?: string;
  id?: string;
  timestamp?: number;
  // intro
  did?: string;
  name?: string;
  signPub?: string;
  needIntro?: boolean;
  // ack / read
  msgId?: string;
  // 群聊（v3.9）
  groupId?: string;
  // signal
  type?: string;
  sdp?: string;
  candidate?: string;
  sdpMid?: string;
  sdpMLineIndex?: number;
}

/**
 * 组装并签名信封。
 * 签名对象 = canonical(除 signature 外的全部字段)。
 */
export function buildEnvelope(input: BuildEnvelopeInput): ChatEnvelope {
  const base: Omit<ChatEnvelope, 'signature'> = {
    protocol: ENVELOPE_PROTOCOL,
    kind: input.kind,
    id: input.id ?? newMessageId(),
    from: input.from,
    to: input.to,
    timestamp: input.timestamp ?? Date.now(),
    ephemeralKey: input.encPub,
    // ⚠️ `ephemeralKey` 是 ChatEnvelope 的必填字段（历史命名），但业务侧读取的是 `encPub`：
    // ChatService 的 intro 交换与 E2EE 会话全部读 `env.encPub`（`!env.encPub` 即把该帧当垃圾丢弃）。
    // 早期只写了 `ephemeralKey`，导致 `env.encPub` 恒为 undefined —— 收端丢弃合法 intro、
    // introState 永远 pending、contact.encPub 永远为空 →「加好友 + 加密收发」整条链路不可用。
    // 两者值相同，同时写入以保持向后兼容。
    encPub: input.encPub,
    sequence: input.sequence,
    ...(input.ciphertext ? { ciphertext: input.ciphertext } : {}),
    ...(input.iv ? { iv: input.iv } : {}),
    ...(input.did ? { did: input.did } : {}),
    ...(input.name ? { name: input.name } : {}),
    ...(input.signPub ? { signPub: input.signPub } : {}),
    ...(input.needIntro !== undefined ? { needIntro: input.needIntro } : {}),
    ...(input.msgId ? { msgId: input.msgId } : {}),
    ...(input.groupId ? { groupId: input.groupId } : {}),
    ...(input.type ? { type: input.type } : {}),
    ...(input.sdp ? { sdp: input.sdp } : {}),
    ...(input.candidate ? { candidate: input.candidate } : {}),
    ...(input.sdpMid ? { sdpMid: input.sdpMid } : {}),
    ...(input.sdpMLineIndex !== undefined ? { sdpMLineIndex: input.sdpMLineIndex } : {}),
  };

  const signature = ed25519Sign(canonicalBytes(base), input.signSeed);
  return { ...base, signature: bytesToBase64(signature) } as ChatEnvelope;
}

/** 取出参与签名的字段（剔除 signature）。 */
export function signableView(env: ChatEnvelope): Record<string, unknown> {
  const { signature: _signature, ...rest } = env;
  return rest as Record<string, unknown>;
}

/**
 * 验签（不做解密）。
 * @param env 信封
 * @param peerSignPubB64 对方 Ed25519 签名公钥（base64）
 */
export function verifyEnvelope(env: ChatEnvelope, peerSignPubB64: string): boolean {
  if (!env || typeof env.signature !== 'string') return false;
  if (env.protocol !== ENVELOPE_PROTOCOL) return false;
  try {
    const sig = base64ToBytes(env.signature);
    const pub = base64ToBytes(peerSignPubB64);
    return ed25519Verify(sig, canonicalBytes(signableView(env)), pub);
  } catch {
    return false;
  }
}

/** 信封结构合法性检查（防畸形输入打爆后续逻辑）。 */
export function assertEnvelopeShape(env: unknown): ChatEnvelope {
  if (!env || typeof env !== 'object') throw new MeshLinkError('E_SIGNATURE_INVALID', '信封不是对象');
  const e = env as Partial<ChatEnvelope>;
  if (e.protocol !== ENVELOPE_PROTOCOL) throw new MeshLinkError('E_SIGNATURE_INVALID', '协议标识不匹配');
  if (!e.id || !e.from || !e.to) throw new MeshLinkError('E_SIGNATURE_INVALID', '信封缺少必要字段');
  if (typeof e.signature !== 'string' || e.signature.length === 0) {
    throw new MeshLinkError('E_SIGNATURE_INVALID', '信封缺少签名');
  }
  return e as ChatEnvelope;
}

/**
 * 打开信封：**先验签后解密**。
 *
 * @param env 收到的信封
 * @param peerSignPubB64 对方签名公钥（base64）
 * @param cipher ISessionCipher（可选；不传则只验签不解密）
 * @param peerEncPubB64 对方加密公钥（解密时必填）
 */
export async function openEnvelope(
  env: ChatEnvelope,
  peerSignPubB64: string,
  cipher?: { decrypt(e: ChatEnvelope, peer: { encPub: string; signPub: string }): Promise<Uint8Array> },
  peerEncPubB64?: string,
): Promise<{ envelope: ChatEnvelope; plaintext?: Uint8Array }> {
  assertEnvelopeShape(env);
  if (!verifyEnvelope(env, peerSignPubB64)) {
    throw new MeshLinkError('E_SIGNATURE_INVALID');
  }
  if (!cipher || !peerEncPubB64 || !env.ciphertext) {
    return { envelope: env };
  }
  const plaintext = await cipher.decrypt(env, { encPub: peerEncPubB64, signPub: peerSignPubB64 });
  return { envelope: env, plaintext };
}
